import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", paid: false });
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", paid: false, msg: "Missing session id" }); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          clear();
          setState({ status: "ok", paid: true, order_id: r.data.order_id });
          return;
        }
        if (r.data.status === "expired") {
          setState({ status: "expired", paid: false, msg: "Session expired" });
          return;
        }
        if (++attempts >= max) {
          setState({ status: "timeout", paid: false, msg: "Still processing. Check email shortly." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", paid: false, msg: e.response?.data?.detail || "Status check failed." });
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-8">
      <div className="border border-zinc-900 p-12 max-w-md w-full text-center" data-testid="checkout-success-card">
        {state.status === "polling" && <>
          <Spinner size={48} weight="bold" className="mx-auto animate-spin" />
          <h1 className="text-3xl font-black tracking-tighter uppercase font-[Outfit] mt-4">Confirming Payment</h1>
          <p className="text-sm text-zinc-600 mt-2">Please wait while we verify with Stripe…</p>
        </>}
        {state.status === "ok" && <>
          <CheckCircle size={56} weight="duotone" className="mx-auto text-[#0055ff]" />
          <h1 className="text-3xl font-black tracking-tighter uppercase font-[Outfit] mt-4" data-testid="order-success-heading">Order Confirmed</h1>
          <p className="text-sm text-zinc-600 mt-2">Thanks for shopping Stuffly. Order #{state.order_id?.slice(0, 8)}.</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Link to="/products" className="btn-brutal">Continue Shopping</Link>
            <Link to="/account" className="btn-outline">My Orders</Link>
          </div>
        </>}
        {(state.status === "error" || state.status === "expired" || state.status === "timeout") && <>
          <XCircle size={56} weight="duotone" className="mx-auto text-[#ff3333]" />
          <h1 className="text-3xl font-black tracking-tighter uppercase font-[Outfit] mt-4">Heads Up</h1>
          <p className="text-sm text-zinc-600 mt-2">{state.msg}</p>
          <Link to="/cart" className="btn-brutal btn-red mt-6 inline-flex">Return To Cart</Link>
        </>}
      </div>
    </div>
  );
}
